import { z } from 'zod';

import { CatalystsSchema } from './catalyst.schema';
import { AnalystConsensusSchema } from './consensus.schema';
import { StockFinancialsSchema } from './financials.schema';
import { TickerSchema } from './primitives';
import { SecuritySchema } from './security.schema';
import { SeriesStatsSchema } from './series.schema';

/**
 * Stock-screen profile for GET /v1/securities/:ticker (plan §6.6):
 * security row + 52w range stats + F15 consensus|null + catalysts[] +
 * financials|null + optional alias affordance fields on former-alias 200s.
 */
/**
 * Last and previous close, as a property of the SECURITY rather than of a chart
 * window (ADR-0032 established this shape for the market snapshot).
 *
 * The header's price change was previously derived from whichever series the
 * range selector had loaded, so it moved when the user clicked 3Y and — because
 * a 3Y window is ~1 bar per week, not per day — reported a weekly move under a
 * daily label. Carrying the pair here makes the number a fact about the quote
 * that no navigational state can change.
 *
 * `prevClose` is null when only one close is servable (a first listing day, or a
 * window that returned a single bar). It is never 0: a close of 0 would be a
 * real observation (R2b).
 */
export const QuoteCloseSchema = z.object({
  lastClose: z.number(),
  prevClose: z.number().nullable(),
});
export type QuoteClose = z.infer<typeof QuoteCloseSchema>;

export const SecurityProfileSchema = z.object({
  security: SecuritySchema,
  /** Window-independent close pair backing the header's price change. */
  quote: QuoteCloseSchema,
  /** 52-week range and series extrema (F12/F13). */
  stats: SeriesStatsSchema,
  /** Live v1: null; synthetic: seeded AnalystConsensus. */
  consensus: AnalystConsensusSchema.nullable(),
  /** Live v1: []; synthetic: four seeded headlines. */
  catalysts: CatalystsSchema,
  /**
   * Statement ratios + annual series (ADR-0034 · ADR-0035). Null when no
   * snapshot is servable at asOf — an empty-honest panel, never fabricated years.
   */
  financials: StockFinancialsSchema.nullable(),
  /** Normalized requested path alias when it differs from the open ticker. */
  requestedTicker: TickerSchema.optional(),
  /** Canonical active ticker when the request used a former alias. */
  canonicalTicker: TickerSchema.optional(),
});
export type SecurityProfile = z.infer<typeof SecurityProfileSchema>;
